import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarSync, Link2, Unplug, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConfirmDelete } from "./common";
import { api, formatApiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/constants";

const STATUS = {
  connected: ["Connecté", "text-emerald-300"],
  disconnected: ["Non connecté", "text-slate-400"],
  reauth_required: ["Nouvelle autorisation nécessaire", "text-amber-300"],
};

const Conflicts = ({ items, onResolve }) => (
  <div className="space-y-2" data-testid="outlook-conflicts">
    <h3 className="text-sm font-semibold text-white">Conflits à résoudre ({items.length})</h3>
    {items.length === 0 && <p className="text-xs text-slate-400">Aucun conflit.</p>}
    {items.map((c) => (
      <div key={c.id} className="rounded-lg border border-amber-400/30 bg-amber-400/5 p-3 text-sm" data-testid={`outlook-conflict-${c.id}`}>
        <p className="font-medium text-white">{c.title || "Intervention"} · {c.kind === "deleted_remote" ? "supprimé dans Outlook mais modifié dans DTech42" : "modifié des deux côtés"}</p>
        <p className="text-xs text-slate-300">DTech42 : {c.app_state ? `${fmtDateTime(c.app_state.start)} · ${c.app_state.duration} min · ${c.app_state.location}` : "non planifiée"}</p>
        <p className="text-xs text-slate-300">Outlook : {c.remote_state ? `${fmtDateTime(c.remote_state.start)} · ${c.remote_state.duration} min · ${c.remote_state.location}` : "rendez-vous supprimé"}</p>
        <div className="mt-2 flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" onClick={() => onResolve(c.id, "app")} data-testid={`outlook-conflict-keep-app-${c.id}`}>Garder DTech42</Button>
          <Button size="sm" variant="secondary" onClick={() => onResolve(c.id, "outlook")} data-testid={`outlook-conflict-keep-outlook-${c.id}`}>Garder Outlook</Button>
        </div>
      </div>
    ))}
  </div>
);

const FirstSync = ({ preview, onConfirm, busy }) => (
  <div className="space-y-2 rounded-lg border border-cyan-400/30 bg-cyan-400/5 p-4" data-testid="outlook-first-sync">
    <h3 className="text-sm font-semibold text-white">Aperçu de la première synchronisation</h3>
    <p className="text-xs text-slate-300">Rien n'a encore été écrit dans Outlook. {preview.external_count} événement(s) Outlook lus. {preview.to_create.length} rendez-vous seront créés dans votre calendrier :</p>
    <ul className="max-h-48 space-y-1 overflow-y-auto text-xs text-slate-200">
      {preview.to_create.map((p) => <li key={p.id}>· {fmtDateTime(p.scheduled_at)} — {p.title} ({p.client_name})</li>)}
    </ul>
    <Button onClick={onConfirm} disabled={busy} className="dt-glow" data-testid="outlook-initialize-button">Valider et lancer la synchronisation</Button>
  </div>
);

export const OutlookSection = () => {
  const [st, setSt] = useState(null);
  const [conflicts, setConflicts] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmOff, setConfirmOff] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/outlook/status");
    setSt(data);
    if (data.status === "connected") {
      setConflicts((await api.get("/outlook/conflicts")).data);
      setPreview(data.initialized ? null : (await api.get("/outlook/preview")).data);
    }
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const act = async (fn, ok) => {
    setBusy(true);
    try {
      const r = await fn();
      if (r?.data?.error) toast.error(r.data.error);
      else if (ok) toast.success(ok);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  const connect = () => act(async () => { window.location.href = (await api.post("/outlook/connect")).data.url; });
  if (!st) return null;
  const [label, color] = STATUS[st.status] || STATUS.disconnected;

  return (
    <section className="dt-card max-w-3xl space-y-4 p-6" data-testid="outlook-section">
      <div className="flex items-center gap-2"><CalendarSync className="h-5 w-5 text-cyan-300" /><h2 className="font-display text-lg font-semibold text-white">Calendrier Outlook</h2></div>
      {!st.configured && <p className="text-sm text-amber-300" data-testid="outlook-not-configured">Intégration non configurée sur ce serveur : renseignez les variables MS_* (voir deploy/README-OUTLOOK.md).</p>}
      <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
        <div><dt className="text-xs text-slate-400">État</dt><dd className={color} data-testid="outlook-status">{label}</dd></div>
        <div><dt className="text-xs text-slate-400">Compte / calendrier</dt><dd className="text-white" data-testid="outlook-account">{st.email || st.allowed_email || "—"} · {st.calendar}</dd></div>
        <div><dt className="text-xs text-slate-400">Dernière synchronisation réussie</dt><dd className="text-white" data-testid="outlook-last-sync">{st.last_sync_at ? fmtDateTime(st.last_sync_at) : "Jamais"}</dd></div>
        <div><dt className="text-xs text-slate-400">Opérations en attente</dt><dd className="text-white">{st.pending_ops}</dd></div>
      </dl>
      {st.last_error && <p className="rounded-lg border border-red-400/30 bg-red-400/5 p-3 text-sm text-red-200" data-testid="outlook-error">{st.last_error}</p>}
      {st.error_ops.length > 0 && <p className="text-xs text-red-300">{st.error_ops.length} opération(s) en échec : {st.error_ops[0].error} — corrigez puis cliquez « Synchroniser maintenant ».</p>}
      <div className="flex flex-wrap gap-2">
        {st.status !== "connected" ? (
          <Button onClick={connect} disabled={busy || !st.configured} className="dt-glow" data-testid="outlook-connect-button"><Link2 className="mr-1 h-4 w-4" />Connecter Outlook</Button>
        ) : (
          <>
            <Button onClick={() => act(() => api.post("/outlook/sync"), "Synchronisation terminée")} disabled={busy} className="dt-glow" data-testid="outlook-sync-button"><RefreshCw className="mr-1 h-4 w-4" />Synchroniser maintenant</Button>
            <Button variant="ghost" onClick={() => setConfirmOff(true)} disabled={busy} data-testid="outlook-disconnect-button"><Unplug className="mr-1 h-4 w-4" />Déconnecter</Button>
          </>
        )}
      </div>
      {st.status === "connected" && preview && <FirstSync preview={preview} busy={busy} onConfirm={() => act(() => api.post("/outlook/initialize"), "Synchronisation activée")} />}
      {st.status === "connected" && <Conflicts items={conflicts} onResolve={(id, choice) => act(() => api.post(`/outlook/conflicts/${id}/resolve`, { choice }), "Conflit résolu")} />}
      <p className="text-xs text-slate-400">Période synchronisée : 30 jours passés à 12 mois à venir, renouvelée chaque semaine. Synchronisation automatique toutes les 5 minutes sur le serveur.</p>
      <ConfirmDelete open={confirmOff} onOpenChange={setConfirmOff} label="la connexion Outlook" description="La synchronisation s'arrête et les jetons Microsoft sont effacés. Les rendez-vous déjà présents dans Outlook sont conservés." onConfirm={() => act(() => api.post("/outlook/disconnect"), "Outlook déconnecté")} />
    </section>
  );
};
